import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { StaffAvailabilityPublic } from "@/components/calendar";
import { LanguageSelector } from "@/components/ui/language-selector";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Phone,
  Calendar,
  MessageSquare,
  Clock,
  Mail,
  CheckCircle,
  Loader2,
  ArrowLeft
} from "lucide-react";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { invokeFunction, invokeFunctionSilent } from '@/lib/cloudrun';

const translations = {
  en: {
    title: "Contact Our Team",
    subtitle: "We're here to help you succeed in your business acquisition journey",
    callTeam: "Call the Team",
    scheduleCall: "Schedule a Call",
    sendMessage: "Send a Message",
    availableSlots: "Available Time Slots",
    noSlotsAvailable: "No slots available at the moment",
    bookSlot: "Book This Slot",
    yourInfo: "Your Information",
    name: "Full Name",
    email: "Email",
    phone: "Phone",
    reason: "How can we help you?",
    submit: "Submit Request",
    sending: "Sending...",
    callNow: "Call Now",
    avgWaitTime: "Average wait time",
    minutes: "minutes",
    officeHours: "Office Hours",
    mondayFriday: "Monday - Friday",
    saturday: "Saturday",
    sunday: "Sunday (Closed)",
    emailUs: "Email Us",
    booked: "Booked!",
    bookingSuccess: "Your call request has been sent. We'll confirm shortly by email.",
    selectSlot: "Select a time slot",
    messageSent: "Message sent!",
    messageSuccess: "Thank you! We've sent you a confirmation email. Our team will get back to you within 24-48 hours.",
    messageError: "Failed to send message. Please try again.",
    teamAvailability: "Team Availability",
    viewTeamCalendar: "View our team's real-time availability",
    back: "Back",
    selectSlotFirst: "Please select a time slot",
    fillRequiredFields: "Please fill all required fields",
    bookingFailed: "Booking failed. Please try again.",
  },
  fr: {
    title: "Contactez Notre Équipe",
    subtitle: "Nous sommes là pour vous accompagner dans votre parcours d'acquisition",
    callTeam: "Appeler l'équipe",
    scheduleCall: "Planifier un appel",
    sendMessage: "Envoyer un message",
    availableSlots: "Créneaux disponibles",
    noSlotsAvailable: "Aucun créneau disponible pour le moment",
    bookSlot: "Réserver ce créneau",
    yourInfo: "Vos informations",
    name: "Nom complet",
    email: "Courriel",
    phone: "Téléphone",
    reason: "Comment pouvons-nous vous aider?",
    submit: "Envoyer la demande",
    sending: "Envoi en cours...",
    callNow: "Appeler maintenant",
    avgWaitTime: "Temps d'attente moyen",
    minutes: "minutes",
    officeHours: "Heures d'ouverture",
    mondayFriday: "Lundi - Vendredi",
    saturday: "Samedi",
    sunday: "Dimanche (Fermé)",
    emailUs: "Courriel",
    booked: "Réservé!",
    bookingSuccess: "Votre demande de rendez-vous a été envoyée. Nous confirmerons sous peu par courriel.",
    selectSlot: "Sélectionner un créneau",
    messageSent: "Message envoyé!",
    messageSuccess: "Merci! Nous vous avons envoyé un courriel de confirmation. Notre équipe vous répondra dans les 24 à 48 heures.",
    messageError: "Échec de l'envoi du message. Veuillez réessayer.",
    teamAvailability: "Disponibilités de l'Équipe",
    viewTeamCalendar: "Consultez les disponibilités en temps réel de notre équipe",
    back: "Retour",
    selectSlotFirst: "Veuillez sélectionner un créneau",
    fillRequiredFields: "Veuillez remplir tous les champs requis",
    bookingFailed: "Échec de la réservation. Veuillez réessayer.",
  },
};

export default function Contact() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language] || translations.en;
  const stats = { avgWaitTime: 2 };

  const [selectedCalendarSlot, setSelectedCalendarSlot] = useState<string | null>(null);
  const [selectedSlotEnd, setSelectedSlotEnd] = useState<Date | null>(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [bookingInfo, setBookingInfo] = useState({
    name: '',
    email: '',
    phone: '',
    reason: '',
  });
  const [isBooking, setIsBooking] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [availabilityLangFilter, setAvailabilityLangFilter] = useState<string>('all');

  const [messageForm, setMessageForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);

  // ── Sélection d'un créneau dans la grille StaffAvailabilityPublic ──────
  // Retient maintenant les vrais staffId (pas seulement les noms), requis
  // pour que bookStaffCall puisse notifier les bonnes personnes.
  const handleCalendarSlotSelect = (slot: { start: Date; end: Date; staffIds: string[] }) => {
    setSelectedCalendarSlot(slot.start.toISOString());
    setSelectedSlotEnd(slot.end);
    setSelectedStaffIds(slot.staffIds);
    setBookingInfo(prev => ({
      ...prev,
      reason: `${language === 'fr' ? 'Rendez-vous demandé pour' : 'Meeting requested for'} ${slot.start.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`
    }));
  };

  const dateLocale = language === 'fr' ? fr : enUS;

  // ── Confirmer la réservation — appelle la Cloud Function publique
  // bookStaffCall, qui écrit dans Firestore via SDK Admin (donc aucune
  // authentification requise côté visiteur) et notifie le staff éligible.
  const handleBookSlot = async () => {
    if (!bookingInfo.name || !bookingInfo.email) {
      toast.error(t.fillRequiredFields);
      return;
    }

    if (!selectedCalendarSlot || !selectedSlotEnd || selectedStaffIds.length === 0) {
      toast.error(t.selectSlotFirst);
      return;
    }

    setIsBooking(true);

    try {
      await invokeFunction('book-staff-call', {
        visitorName: bookingInfo.name,
        visitorEmail: bookingInfo.email,
        visitorPhone: bookingInfo.phone,
        reason: bookingInfo.reason,
        slotStart: selectedCalendarSlot,
        slotEnd: selectedSlotEnd.toISOString(),
        candidateStaffIds: selectedStaffIds,
        language,
      }, { requireAuth: false });

      setBookingComplete(true);
      toast.success(t.bookingSuccess);
    } catch (error) {
      console.error('Error booking slot:', error);
      toast.error(t.bookingFailed);
    } finally {
      setIsBooking(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageForm.name || !messageForm.email || !messageForm.message) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs' : 'Please fill all fields');
      return;
    }

    setIsSendingMessage(true);

    try {
      await invokeFunctionSilent('send-contact-confirmation', {
  name: messageForm.name, email: messageForm.email, message: messageForm.message, language: language,
});
      setMessageSent(true);
      setMessageForm({ name: '', email: '', message: '' });
      toast.success(t.messageSent);
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast.error(t.messageError);
    } finally {
      setIsSendingMessage(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] text-white py-16">
        <div className="container mx-auto px-4">
          {/* Top bar with Logo, Back + Language */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img src="/noBgColor.png" alt="AcquizGroup" className="ag-logo-header" />
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-white hover:bg-white/10 gap-1.5"
              >
                <ArrowLeft className="h-4 w-4" />
                {t.back}
              </Button>
            </div>
            <LanguageSelector variant="light" />
          </div>

          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">{t.subtitle}</p>

            {/* Quick Stats - only wait time */}
            <div className="flex justify-center gap-8 mt-8">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#CEAB65]" />
                <span className="text-lg">~{stats.avgWaitTime || 2} {t.minutes} {t.avgWaitTime}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Call Options */}
          <div className="lg:col-span-2 space-y-6">
            {/* Instant Call Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-[#CEAB65]" />
                  {t.callTeam}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? 'Parlez directement avec un membre de notre équipe'
                    : 'Speak directly with a team member'}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="bg-[#CEAB65] hover:bg-[#CEAB65]/90 text-[#1D314D]"
                  onClick={() => window.location.href = 'tel:+13213217909'}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  {t.callNow}
                </Button>
              </CardContent>
            </Card>

            {/* Schedule Call Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#CEAB65]" />
                  {t.scheduleCall}
                </CardTitle>
                <CardDescription>{t.selectSlot}</CardDescription>
              </CardHeader>
              <CardContent>
                {bookingComplete ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">{t.booked}</h3>
                    <p className="text-muted-foreground">{t.bookingSuccess}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(selectedCalendarSlot) && (
                      <div className="space-y-4 pt-4 border-t">
                        <h4 className="font-medium">{t.yourInfo}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="name">{t.name} *</Label>
                            <Input
                              id="name"
                              value={bookingInfo.name}
                              onChange={(e) => setBookingInfo(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Jean Dupont"
                            />
                          </div>
                          <div>
                            <Label htmlFor="email">{t.email} *</Label>
                            <Input
                              id="email"
                              type="email"
                              value={bookingInfo.email}
                              onChange={(e) => setBookingInfo(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="jean@example.com"
                            />
                          </div>
                          <div>
                            <Label htmlFor="phone">{t.phone}</Label>
                            <Input
                              id="phone"
                              value={bookingInfo.phone}
                              onChange={(e) => setBookingInfo(prev => ({ ...prev, phone: e.target.value }))}
                              placeholder="+1 514 555-1234"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="reason">{t.reason}</Label>
                          <Textarea
                            id="reason"
                            value={bookingInfo.reason}
                            onChange={(e) => setBookingInfo(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder={language === 'fr'
                              ? "Je souhaite discuter de l'acquisition d'une entreprise..."
                              : "I would like to discuss acquiring a business..."}
                            rows={3}
                          />
                        </div>
                        <Button
                          onClick={handleBookSlot}
                          disabled={isBooking}
                          className="w-full bg-[#CEAB65] hover:bg-[#CEAB65]/90 text-[#1D314D]"
                        >
                          {isBooking ? '...' : t.bookSlot}
                        </Button>
                      </div>
                    )}
                    {!selectedCalendarSlot && (
                      <p className="text-muted-foreground text-center py-4">
                        {language === 'fr'
                          ? 'Sélectionnez un créneau dans le calendrier ci-dessous pour continuer.'
                          : 'Select a slot in the calendar below to continue.'}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Send Message */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-[#CEAB65]" />
                  {t.sendMessage}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {messageSent ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">{t.messageSent}</h3>
                    <p className="text-muted-foreground">{t.messageSuccess}</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => setMessageSent(false)}
                    >
                      {language === 'fr' ? 'Envoyer un autre message' : 'Send another message'}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSendMessage} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="msg-name">{t.name} *</Label>
                        <Input
                          id="msg-name"
                          placeholder="Jean Dupont"
                          value={messageForm.name}
                          onChange={(e) => setMessageForm(prev => ({ ...prev, name: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="msg-email">{t.email} *</Label>
                        <Input
                          id="msg-email"
                          type="email"
                          placeholder="jean@example.com"
                          value={messageForm.email}
                          onChange={(e) => setMessageForm(prev => ({ ...prev, email: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="message">{t.reason} *</Label>
                      <Textarea
                        id="message"
                        rows={4}
                        value={messageForm.message}
                        onChange={(e) => setMessageForm(prev => ({ ...prev, message: e.target.value }))}
                        placeholder={language === 'fr'
                          ? "Je souhaite discuter de l'acquisition d'une entreprise..."
                          : "I would like to discuss acquiring a business..."}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      className="bg-[#1D314D] hover:bg-[#1D314D]/90"
                      disabled={isSendingMessage}
                    >
                      {isSendingMessage ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t.sending}
                        </>
                      ) : (
                        t.submit
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Language Filter for Availability */}
            <div className="flex items-center gap-3 mb-2">
              <Label className="text-sm font-medium whitespace-nowrap">
                {language === 'fr' ? 'Filtrer par langue :' : 'Filter by language:'}
              </Label>
              <Select value={availabilityLangFilter} onValueChange={setAvailabilityLangFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Toutes les langues' : 'All languages'}</SelectItem>
                  <SelectItem value="fr">🇫🇷 Français</SelectItem>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                  <SelectItem value="es">🇪🇸 Español</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Staff Availability Calendar */}
            <StaffAvailabilityPublic
              onSlotSelect={handleCalendarSlotSelect}
              selectedSlotTime={selectedCalendarSlot}
              filterLanguage={availabilityLangFilter === 'all' ? null : availabilityLangFilter}
            />
          </div>

          {/* Right Column - Contact Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t.officeHours}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.mondayFriday}</span>
                  <span className="font-medium">
                    {language === 'fr' ? '9:00 - 18:00' : '9:00 AM - 6:00 PM'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.saturday}</span>
                  <span className="font-medium">
                    {language === 'fr' ? '10:00 - 14:00' : '10:00 AM - 2:00 PM'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.sunday}</span>
                  <Badge variant="secondary">{language === 'fr' ? 'Fermé' : 'Closed'}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Nous joindre' : 'Contact Us'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-[#CEAB65]" />
                  <a href="tel:+13213217909" className="hover:text-[#CEAB65]">
                    (321) 321-7909
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[#CEAB65]" />
                  <a href="mailto:contact@acquizgroup.com" className="hover:text-[#CEAB65]">
                    contact@acquizgroup.com
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}